import { useState } from 'react';
import {
  services,
  stats,
  whyChooseUs,
  processSteps,
} from '@/mocks/home';

export default function HomePage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const honeypot = formData.get('website_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      return;
    }
    // Simulate form submission success
    setFormStatus('success');
    setFormError('');
    (e.target as HTMLFormElement).reset();
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section id="inicio" className="relative min-h-[600px] md:min-h-[700px] flex items-center">
        <div className="absolute inset-0 z-0">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20air%20conditioning%20unit%20installation%20on%20white%20wall%2C%20clean%20professional%20interior%2C%20bright%20natural%20light%20from%20window%2C%20minimal%20modern%20home%20interior%2C%20soft%20blue%20tones%2C%20editorial%20photography%2C%20high%20quality&width=1600&height=900&seq=hero-airmex-01&orientation=landscape"
            alt="Instalación de aire acondicionado"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-900/85 via-primary-800/70 to-primary-600/50"></div>
        </div>

        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 py-24 md:py-32">
          <div className="max-w-3xl">
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              Instalación de Aires Acondicionados con{' '}
              <span className="text-accent-300">Garantía y Frescura</span>
            </h1>
            <p className="text-white/85 text-base md:text-lg leading-relaxed mb-8 max-w-xl">
              Expertos en climatización residencial y comercial. Instalación, mantenimiento
              y reparación con los más altos estándares de calidad. Tu confort es nuestra prioridad.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="#contacto"
                className="bg-primary-500 text-white px-7 py-3.5 rounded-md font-semibold text-sm hover:bg-primary-600 transition-colors text-center whitespace-nowrap"
              >
                Solicitar Cotización Gratis
              </a>
              <a
                href="#servicios"
                className="bg-white/15 backdrop-blur-sm border border-white/30 text-white px-7 py-3.5 rounded-md font-semibold text-sm hover:bg-white/25 transition-colors text-center whitespace-nowrap"
              >
                Ver Servicios
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-white py-10 md:py-14 border-b border-background-200">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-2 max-w-2xl mx-auto gap-6 md:gap-8">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="text-2xl md:text-4xl font-bold text-primary-600 font-heading mb-1">
                  {stat.value}
                </div>
                <div className="text-foreground-600 text-xs md:text-sm font-medium">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="servicios" className="py-16 md:py-24 bg-background-50">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="text-center max-w-2xl mx-auto mb-12 md:mb-16">
            <span className="inline-block text-primary-600 text-xs font-semibold uppercase tracking-wider mb-3">
              Nuestros Servicios
            </span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
              Soluciones Completas de Climatización
            </h2>
            <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
              Ofrecemos un servicio integral para que tu hogar o negocio disfrute de la temperatura perfecta todo el año.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {services.map((service) => (
              <div
                key={service.id}
                className="group bg-white rounded-lg p-6 md:p-7 border border-background-200 hover:border-primary-300 hover:shadow-lg transition-all duration-300"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-4 group-hover:bg-primary-500 group-hover:text-white transition-colors">
                  <i className={`${service.icon} text-xl`}></i>
                </div>
                <h3 className="font-heading font-semibold text-base md:text-lg text-foreground-950 mb-2">
                  {service.title}
                </h3>
                <p className="text-foreground-600 text-sm leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section id="nosotros" className="py-16 md:py-24 bg-white">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-center">
            {/* Image */}
            <div className="relative">
              <div className="rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20HVAC%20technician%20in%20blue%20uniform%20installing%20modern%20air%20conditioning%20unit%20indoors%2C%20clean%20modern%20home%20interior%2C%20bright%20daylight%2C%20professional%20workmanship%2C%20warm%20neutral%20tones%2C%20editorial%20photography&width=800&height=600&seq=about-airmex-01&orientation=landscape"
                  alt="Técnico de AIRMEX instalando aire acondicionado"
                  className="w-full h-[400px] md:h-[500px] object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 md:bottom-6 md:right-6 bg-primary-500 text-white rounded-lg p-4 md:p-5 shadow-lg">
                <div className="text-2xl md:text-3xl font-bold font-heading">15+</div>
                <div className="text-xs md:text-sm opacity-90">Años de Experiencia</div>
              </div>
            </div>

            {/* Content */}
            <div>
              <span className="inline-block text-primary-600 text-xs font-semibold uppercase tracking-wider mb-3">
                ¿Por Qué Elegirnos?
              </span>
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
                La Mejor Opción para tu Climatización
              </h2>
              <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-8">
                En AIRMEX combinamos experiencia, tecnología y compromiso para ofrecerte el mejor servicio de climatización. Nuestros clientes confían en nosotros porque siempre cumplimos.
              </p>

              <div className="flex flex-col gap-5">
                {whyChooseUs.map((item) => (
                  <div key={item.id} className="flex gap-4">
                    <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                      <i className={`${item.icon} text-lg`}></i>
                    </div>
                    <div>
                      <h4 className="font-heading font-semibold text-base text-foreground-950 mb-1">
                        {item.title}
                      </h4>
                      <p className="text-foreground-600 text-sm leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 md:py-24 bg-background-50">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="text-center max-w-2xl mx-auto mb-12 md:mb-16">
            <span className="inline-block text-primary-600 text-xs font-semibold uppercase tracking-wider mb-3">
              Cómo Trabajamos
            </span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
              Proceso Simple y Efectivo
            </h2>
            <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
              Tres pasos para disfrutar de tu espacio perfectamente climatizado.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {processSteps.map((step, idx) => (
              <div key={idx} className="relative text-center">
                {idx < processSteps.length - 1 && (
                  <div className="hidden md:block absolute top-10 left-[60%] right-[-20%] h-0.5 bg-primary-200"></div>
                )}
                <div className="w-20 h-20 flex items-center justify-center rounded-full bg-primary-500 text-white text-2xl font-bold font-heading mx-auto mb-5 shadow-md">
                  {step.step}
                </div>
                <h3 className="font-heading font-semibold text-lg text-foreground-950 mb-2">
                  {step.title}
                </h3>
                <p className="text-foreground-600 text-sm leading-relaxed max-w-xs mx-auto">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA / Contact Section */}
      <section id="contacto" className="py-16 md:py-24 bg-primary-500">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-center">
            {/* Contact Info */}
            <div>
              <span className="inline-block text-primary-200 text-xs font-semibold uppercase tracking-wider mb-3">
                Contacto
              </span>
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-white mb-4">
                Solicitá tu Cotización Gratis Hoy
              </h2>
              <p className="text-primary-100 text-sm md:text-base leading-relaxed mb-8">
                Completá el formulario y un especialista se pondrá en contacto con vos en menos de 24 horas. Cotización sin compromiso.
              </p>

              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15">
                    <i className="ri-phone-line text-white"></i>
                  </div>
                  <div>
                    <div className="text-xs text-primary-200">Teléfono</div>
                    <a href="tel:+523315263900" className="text-white font-medium text-sm">
                      +52 3315263900
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15">
                    <i className="ri-mail-line text-white"></i>
                  </div>
                  <div>
                    <div className="text-xs text-primary-200">Email</div>
                    <a href="mailto:contacto@airmex.com" className="text-white font-medium text-sm">
                      contacto@airmex.com
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15">
                    <i className="ri-map-pin-line text-white"></i>
                  </div>
                  <div>
                    <div className="text-xs text-primary-200">Dirección</div>
                    <span className="text-white font-medium text-sm">
                      Av. Principal 123, Ciudad Central
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="bg-white rounded-lg p-6 md:p-8 shadow-xl">
              {formStatus === 'success' ? (
                <div className="text-center py-8">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mx-auto mb-4">
                    <i className="ri-check-line text-2xl"></i>
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-foreground-950 mb-2">
                    ¡Mensaje Enviado!
                  </h3>
                  <p className="text-foreground-600 text-sm mb-4">
                    Gracias por contactarnos. Un especialista se comunicará con vos pronto.
                  </p>
                  <button
                    onClick={() => setFormStatus('idle')}
                    className="text-primary-600 text-sm font-medium hover:text-primary-700"
                  >
                    Enviar otro mensaje
                  </button>
                </div>
              ) : (
                <form onSubmit={handleFormSubmit} className="flex flex-col gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                      Nombre Completo
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      className="w-full px-4 py-3 rounded-md border border-background-200 text-sm focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="Tu nombre"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                      Correo Electrónico
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 rounded-md border border-background-200 text-sm focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="tu@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                      Teléfono
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      className="w-full px-4 py-3 rounded-md border border-background-200 text-sm focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="+52 1 234 567 890"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                      Tipo de Servicio
                    </label>
                    <select
                      name="service_type"
                      required
                      className="w-full px-4 py-3 rounded-md border border-background-200 text-sm focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors bg-white"
                    >
                      <option value="">Seleccioná un servicio</option>
                      <option value="instalacion">Instalación de AC</option>
                      <option value="mantenimiento">Mantenimiento</option>
                      <option value="reparacion">Reparación</option>
                      <option value="comercial">Instalación Comercial</option>
                      <option value="otro">Otro</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                      Mensaje
                    </label>
                    <textarea
                      name="message"
                      rows={3}
                      maxLength={500}
                      className="w-full px-4 py-3 rounded-md border border-background-200 text-sm focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                      placeholder="Contanos qué necesitás..."
                    ></textarea>
                    <div className="text-xs text-foreground-400 mt-1">Máximo 500 caracteres</div>
                  </div>

                  {/* Honeypot */}
                  <div className="hp-field">
                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                  </div>

                  {formError && (
                    <div className="text-red-600 text-sm">{formError}</div>
                  )}

                  <button
                    type="submit"
                    className="w-full bg-primary-500 text-white py-3.5 rounded-md font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    Enviar Solicitud
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}